<template>
  <div class="tw-relative">
    <cl-form-input
      :id="props.id"
      v-model:value="inputValue"
      :type="isPasswordVisible ? 'text' : 'password'"
      :placeholder="
        props.placeholder === ''
          ? t('placeholder-text-password')
          : props.placeholder
      "
      :size="props.size"
      :name="props.name"
      :test-id="props.dataTestId"
      :state="props.state"
      :class="props.size === 'large' ? 'tw-pr-11' : 'tw-pr-9'"
      v-bind="attrs"
      @on-keyup="emitKeyup"
    />
    <button
      type="button"
      class="tw-absolute tw-inset-y-0 tw-right-0 tw-text-dorian-gray-200 hover:tw-text-dorian-gray-400"
      :class="
        props.size === 'large'
          ? 'tw-px-4 tw-py-2'
          : props.size === 'small'
            ? 'tw-px-2'
            : 'tw-px-3 tw-py-2'
      "
      :data-test-id="`${dataTestId}-visibility-toggle-button`"
      @click="togglePasswordVisibility"
    >
      <cl-icon-tabler-icon-eye-off
        v-if="isPasswordVisible"
        :data-test-id="`${dataTestId}-visibility-toggle-visible`"
      />
      <cl-icon-tabler-icon-eye
        v-else
        :data-test-id="`${dataTestId}-visibility-toggle-not-visible`"
      />
    </button>
  </div>
</template>

<script setup lang="ts">
import { ref, useAttrs, watch } from "vue";
import { useI18n } from "vue-i18n";

const { t } = useI18n();
const attrs = useAttrs();

interface TPasswordInputProps {
  id?: string;
  name?: string;
  value: string | null;
  size?: string;
  placeholder?: string;
  dataTestId: string;
  state?: boolean | null;
  autoHideTimeout?: number; // If 0 is set, it will ignore the auto hide
}

const props = withDefaults(defineProps<TPasswordInputProps>(), {
  id: "password",
  name: "password",
  size: "medium",
  placeholder: "",
  dataTestId: "password-input",
  state: null,
  autoHideTimeout: 10, // Default auto hide timeout - 10 seconds
});

const inputValue = ref(props.value);

const emit = defineEmits<{
  (event: "update:value", value: string | number): void;
  (event: "on-keyup", value: KeyboardEvent): void;
}>();

const emitKeyup = (value: KeyboardEvent): void => {
  emit("on-keyup", value);
};

const isPasswordVisible = ref(false);
let inputFieldTimeoutUniqueId: ReturnType<typeof setTimeout> | null = null;

const togglePasswordVisibility = () => {
  isPasswordVisible.value = !isPasswordVisible.value;

  if (props.autoHideTimeout > 0) {
    if (isPasswordVisible.value) {
      if (inputFieldTimeoutUniqueId) {
        clearTimeout(inputFieldTimeoutUniqueId);
      }
      inputFieldTimeoutUniqueId = setTimeout(() => {
        isPasswordVisible.value = false;
        inputFieldTimeoutUniqueId = null;
      }, props.autoHideTimeout * 1000);
    } else if (inputFieldTimeoutUniqueId) {
      clearTimeout(inputFieldTimeoutUniqueId);
      inputFieldTimeoutUniqueId = null;
    }
  }
};

watch(
  () => props.value,
  () => {
    inputValue.value = props.value;
  },
);

watch(inputValue, (newValue) => {
  if (!newValue) {
    return;
  }
  emit("update:value", newValue);
});
</script>
